// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Bands and block columns: the drawing laid out the way the site's own
 * Ignition single-line screens lay it out, on one canvas.
 *
 * The plant is drawn as **one column per block**, each a bounded panel with a
 * header, a status pill, a live metering strip and its own rank rows, and the
 * columns are laid side by side. The chain is cut into **two bands** at the
 * output switchgear, one above the other:
 *
 * - *IT Input Switchgear*: MV loop entry, transformer, generator, the input
 *   board, the UPSs and the mechanical branch.
 * - *IT Output Switchgear / PDU / RPP*: the output board, the PDUs, and the RPPs
 *   stacked vertically under each PDU.
 *
 * Ignition shows those as two screens joined by `TO SGO-A` / `CONTINUED FROM
 * SGI-A` stubs. Ours is one stage, so every connection is drawn: a UPS drops
 * from the input band to the output board below it, the bypass beside it, and
 * the MV riser runs from one block's transformer to the next across the column
 * gap. The one thing not drawn is the MV side above the transformers — the
 * meters and feeders are the loop the blocks hang off, and they enter each
 * block as a `From FEEDER-1 · MV loop` stub above its transformer.
 *
 * Each column-band is laid out by the existing `layoutPowerFlow` on its own
 * sub-display, ranked from its own roots so every block's board lands on the
 * same row; this module composes them, stacks terminal leaves under a device,
 * and routes the connections between them. A site with no blocks, or no
 * output board, gets one band, one implicit column and no chrome — the
 * drawing it had before.
 */

import type { PowerFlowDisplay, PowerFlowLoadGroup } from './powerFlowCollapse';
import type { PowerFlowGraph, PowerFlowLink, PowerFlowVertex } from './powerFlowGraph';
import type { PowerFlowInference } from './powerFlowInference';
import type { PowerFlowRanking } from './powerFlowRank';
import {
  DEFAULT_LAYOUT_OPTIONS,
  layoutPowerFlow,
  portOf,
  type PowerFlowLayout,
  type PowerFlowLayoutOptions,
  type PowerFlowNode,
  type PowerFlowRankRow,
} from './powerFlowLayout';
import {
  BAR_EXTENSION_OVERHANG,
  buildBridgeConductor,
  type BarTap,
  buildStackConductor,
  routePowerFlow,
  type BridgeRouting,
  type PowerFlowConductor,
} from './powerFlowRouting';

export type BandId = 'input' | 'output';

/** A conductor from the undrawn MV side, drawn as a labelled stub. */
export interface SheetStub {
  /** The drawn node the stub attaches to. */
  nodeId: string;
  /** What is at the other end: `FEEDER-1`. */
  label: string;
}

export interface SheetColumn {
  /** `block:A`, or `column:other` for assets in no block. */
  id: string;
  band: BandId;
  /** `Block A`. */
  title: string;
  /** The board whose readings fill this column's metering strip in this band. */
  meterBoardId?: string;
  /** Drawn node ids in this column and band, stacked leaves included. */
  memberIds: string[];
  /** Asset ids behind the drawn nodes, for the status pill. */
  assetIds: string[];
  /** MV-side entries into this column. */
  stubs: SheetStub[];
  /** `From FEEDER-1 · MV loop`, when the MV side enters here. */
  continuation?: string;
  /** Column frame, in stage coordinates. */
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface SheetBand {
  id: BandId;
  title: string;
  y: number;
  h: number;
}

export interface PowerFlowSheet {
  layout: PowerFlowLayout;
  conductors: PowerFlowConductor[];
  /** Empty when the drawing is the unsplit fallback. */
  columns: SheetColumn[];
  bands: SheetBand[];
  /** Node ids drawn stacked under a parent, so the edge layer can class them. */
  stackedIds: Set<string>;
  /** Link ids drawn between columns or bands. */
  bridgeLinkIds: Set<string>;
}

/**
 * One tab's worth of the plant: a pair of blocks, or the odd one left over.
 *
 * Eight blocks side by side is a drawing nobody reads without panning; two is
 * the widest pair that fits and the pairing an operator already thinks in
 * (A/B share a feeder group, C/D the next). Whole-site totals stay on the KPI
 * strip, which is not paged.
 */
export interface SheetPage {
  index: number;
  /** `Block A · B`, or `Block G` for a leftover. */
  title: string;
  blockIds: string[];
}

export interface PowerFlowSheets {
  /** True when the plant was split into block columns and two bands. */
  split: boolean;
  /** Empty when there is nothing to page: no blocks, or one page's worth. */
  pages: SheetPage[];
  /** Index into `pages` of the one drawn. 0 when unpaged. */
  pageIndex: number;
  sheet: PowerFlowSheet;
}

/** Blocks per tab. */
export const BLOCKS_PER_PAGE = 2;

/** Chrome dimensions, in stage px. Mirrored by `SldSheetChrome`'s CSS. */
export const SHEET_CHROME = {
  /** Block title and status pill. */
  headerHeight: 40,
  /** `From … MV loop` line and the top stubs. */
  continuationHeight: 40,
  /** Below the last row of a band. */
  footerHeight: 24,
  /** Between columns. */
  columnGap: 32,
  /** Between the two bands; the cross-band conductors run through it. */
  bandGap: 96,
  /** Left of the columns, for the band titles. */
  bandTitleWidth: 28,
  /** Indent of a stacked child from its parent's left edge. */
  stackIndent: 28,
  /** Vertical gap between stacked children, and above the first. */
  stackGap: 14,
  /** Where the stack trunk leaves the parent, from its left edge. */
  stackTrunk: 12,
} as const;

export const BAND_TITLES: Record<BandId, string> = {
  input: 'IT Input Switchgear',
  output: 'IT Output Switchgear · PDU · RPP',
};

export const OTHER_COLUMN = 'column:other';
/** Not drawn: the meters and feeders above the transformers. See `columnOf`. */
const MV_COLUMN = 'column:mv';

const SOURCE_LIKE = new Set(['utility', 'generator', 'battery', 'transformer']);

/**
 * How far past its end a bar may be extended for a drop with no room on it:
 * twice the drop's clearance from the last load's box, which still sits
 * inside the column's padding.
 */
const BAR_EXTENSION = 32;

/** How far a bridge drops out of its source before turning toward a gutter. */
const BRIDGE_STUB = 14;

/**
 * The block a drawn node is in, or undefined.
 *
 * An asset's block is the inference's. A collapsed group's is the block its
 * parents share — and only then: a run of racks dual-corded across blocks A
 * and C (GA-10's are) is in neither, and putting it in `parentIds[0]`'s block
 * drew it inside A's column with a conductor from C running the width of the
 * drawing. It belongs in the neutral column, between the two.
 */
export const blockOfDrawn = (
  display: Pick<PowerFlowDisplay, 'groupsById'>,
  inference: Pick<PowerFlowInference, 'blockOf'>,
  id: string
): string | undefined => {
  const group = display.groupsById.get(id);
  if (!group) return inference.blockOf.get(id);
  // Every parent has to resolve to the same block; one unassigned feeder is
  // enough to make the group belong to no block.
  const blocks = group.parentIds.map(parentId => inference.blockOf.get(parentId));
  const [first] = blocks;
  return first !== undefined && blocks.every(blockId => blockId === first) ? first : undefined;
};

const naturalCompare = new Intl.Collator('en', { numeric: true, sensitivity: 'base' }).compare;

interface BuildSheetsOptions {
  layout?: Partial<PowerFlowLayoutOptions>;
  /** Short human name for an id, for stub text. Defaults to the id. */
  labelOf?: (_id: string) => string;
  /**
   * Which tab of blocks to draw; see `SheetPage`. Out of range is clamped, so
   * a stored tab survives a site whose block count shrank. The `Other assets`
   * column is drawn on every tab — it is what the unlettered assets hang off.
   */
  pageIndex?: number;
}

/**
 * Lay the plant out as bands of block columns.
 *
 * Deterministic: columns are in letter order, members in the layout's own
 * order, stubs sorted by label.
 */
export const buildPowerFlowSheets = (
  graph: PowerFlowGraph,
  ranking: PowerFlowRanking,
  display: PowerFlowDisplay,
  inference: PowerFlowInference,
  options: BuildSheetsOptions = {}
): PowerFlowSheets => {
  const layoutOptions: PowerFlowLayoutOptions = { ...DEFAULT_LAYOUT_OPTIONS, ...options.layout };
  const labelOf = options.labelOf ?? ((id: string) => id);

  const split = inference.blocks.length > 0 && inference.outputBoardIds.length > 0;
  if (!split) {
    const layout = layoutPowerFlow(display, options.layout);
    const conductors = routePowerFlow(layout, display.treeLinks, display.cycleLinks);
    return {
      split: false,
      pages: [],
      pageIndex: 0,
      sheet: {
        layout,
        conductors,
        columns: [],
        bands: [],
        stackedIds: new Set(),
        bridgeLinkIds: new Set(),
      },
    };
  }

  const pages: SheetPage[] = [];
  for (let i = 0; i < inference.blocks.length; i += BLOCKS_PER_PAGE) {
    const chunk = inference.blocks.slice(i, i + BLOCKS_PER_PAGE);
    pages.push({
      index: pages.length,
      title: `Block ${chunk.map(block => block.letter).join(' · ')}`,
      blockIds: chunk.map(block => block.id),
    });
  }
  const paged = pages.length > 1;
  const pageIndex = paged ? Math.min(Math.max(options.pageIndex ?? 0, 0), pages.length - 1) : 0;
  const visibleBlocks = paged ? new Set(pages[pageIndex].blockIds) : undefined;

  const { verticesById, groupsById, treeLinks, cycleLinks } = display;

  // --- Where each drawn vertex goes ---------------------------------------
  // The meters, feeders and anything else on the MV side of the transformers
  // are the loop the blocks hang off. They are not drawn; they become the
  // `From … MV loop` stub above each transformer. That side is identified
  // *positively*, by topology: a source-like asset in no block whose every
  // outgoing path reaches a transformer before any board or load — it sits
  // upstream of the block transformers, so it is the loop. A generator or a
  // battery in no block that feeds a board directly is not the loop, it is an
  // asset with no letter, and it is drawn in the `Other assets` column and
  // wired to its board. Classing every unlettered source as MV made a genset
  // called `GEN-1` vanish into a `From GEN-1 · MV loop` stub.
  const mvSideMemo = new Map<string, boolean>();
  const isMvSide = (id: string, trail: Set<string>): boolean => {
    const memo = mvSideMemo.get(id);
    if (memo !== undefined) return memo;
    const vertex = verticesById.get(id);
    if (!vertex || blockOfDrawn(display, inference, id)) return false;
    if (vertex.role !== 'source' && !SOURCE_LIKE.has(vertex.symbol)) return false;
    const downstream = vertex.out.filter(child => verticesById.has(child));
    if (downstream.length === 0) return false;
    trail.add(id);
    let cut = false;
    const result = downstream.every(child => {
      if (trail.has(child)) {
        cut = true;
        return true;
      }
      return verticesById.get(child)?.symbol === 'transformer' || isMvSide(child, trail);
    });
    trail.delete(id);
    // A verdict that leaned on a loop back onto the current path is partial;
    // remember only complete walks.
    if (!cut) mvSideMemo.set(id, result);
    return result;
  };
  const columnById = new Map<string, string>();
  for (const id of Array.from(verticesById.keys())) {
    const blockId = blockOfDrawn(display, inference, id);
    columnById.set(id, blockId ?? (isMvSide(id, new Set()) ? MV_COLUMN : OTHER_COLUMN));
  }
  const columnOf = (id: string): string => columnById.get(id) ?? OTHER_COLUMN;

  const children = new Map<string, string[]>();
  const parents = new Map<string, string[]>();
  for (const id of Array.from(verticesById.keys())) {
    children.set(id, []);
    parents.set(id, []);
  }
  for (const link of treeLinks) {
    children.get(link.source)?.push(link.target);
    parents.get(link.target)?.push(link.source);
  }

  // Everything at or below an output board is in the output band.
  const outputSide = new Set<string>();
  const pending = inference.outputBoardIds.filter(id => verticesById.has(id));
  while (pending.length > 0) {
    const id = pending.pop() as string;
    if (outputSide.has(id)) continue;
    outputSide.add(id);
    for (const child of children.get(id) ?? []) pending.push(child);
  }
  const bandOf = (id: string): BandId => (outputSide.has(id) ? 'output' : 'input');

  const visible = (columnId: string): boolean =>
    columnId !== MV_COLUMN &&
    (!visibleBlocks || columnId === OTHER_COLUMN || visibleBlocks.has(columnId));
  const drawn = Array.from(verticesById.keys()).filter(id => visible(columnOf(id)));
  const drawnSet = new Set(drawn);

  const blockTitle = (columnId: string): string => {
    if (columnId === OTHER_COLUMN) return 'Other assets';
    const block = inference.blocks.find(b => b.id === columnId);
    return block ? `Block ${block.letter}` : columnId;
  };
  const columnOrder = (a: string, b: string): number => {
    if (a === OTHER_COLUMN) return 1;
    if (b === OTHER_COLUMN) return -1;
    return naturalCompare(a, b);
  };
  const columnIds = Array.from(new Set(drawn.map(columnOf))).sort(columnOrder);

  // --- Lay out every column-band on its own ---------------------------------
  interface Cell {
    columnId: string;
    band: BandId;
    memberIds: string[];
    layout: PowerFlowLayout;
    treeLinks: PowerFlowLink[];
    cycleLinks: PowerFlowLink[];
    /** Parent → stacked children, in draw order. */
    stacks: Map<string, string[]>;
    rankCount: number;
  }
  const cells: Cell[] = [];

  for (const band of ['input', 'output'] as BandId[]) {
    for (const columnId of columnIds) {
      const members = drawn.filter(id => columnOf(id) === columnId && bandOf(id) === band);
      if (members.length === 0) continue;
      const memberSet = new Set(members);
      const colTree = treeLinks.filter(l => memberSet.has(l.source) && memberSet.has(l.target));
      const colCycle = cycleLinks.filter(l => memberSet.has(l.source) && memberSet.has(l.target));

      // Rank from the column's own roots. The plant's longest-path rank is
      // what the single drawing needs, but the MV riser loops through the
      // transformers in a chain — TXMR-1 → TXMR-4 → TXMR-7 — which pushes
      // block D one rank below block A and block G two, and the boards stop
      // lining up. Inside a column the riser is a connection drawn across the
      // gap, so the column's own depth is the right one.
      const localRank = rankLocally(members, colTree);
      const rankCount = Math.max(...Array.from(localRank.values())) + 1;

      // Leaves under a device stack vertically: the RPPs under a PDU. Only
      // under a box — a bus keeps its taps, that is what a bar is for.
      const colChildren = new Map<string, string[]>();
      for (const l of colTree) {
        const list = colChildren.get(l.source) ?? [];
        list.push(l.target);
        colChildren.set(l.source, list);
      }
      const stacks = new Map<string, string[]>();
      const stacked = new Set<string>();
      for (const parentId of members) {
        const parent = verticesById.get(parentId);
        if (!parent || parent.role === 'bus') continue;
        const kids = colChildren.get(parentId) ?? [];
        if (kids.length < 2) continue;
        if (!kids.every(kid => (colChildren.get(kid) ?? []).length === 0)) continue;
        // A child fed from two parents belongs to neither stack.
        if (!kids.every(kid => (parents.get(kid) ?? []).length === 1)) continue;
        const ordered = kids.slice().sort((a, b) => naturalCompare(labelOf(a), labelOf(b)));
        stacks.set(parentId, ordered);
        for (const kid of ordered) stacked.add(kid);
      }

      const laidOut = members.filter(id => !stacked.has(id));
      const sub = subDisplay(display, laidOut, colTree, colCycle, localRank, rankCount, stacked);
      const layout = layoutPowerFlow(sub, options.layout);
      cells.push({
        columnId,
        band,
        memberIds: members,
        layout,
        treeLinks: colTree,
        cycleLinks: colCycle,
        stacks,
        rankCount,
      });
    }
  }

  // --- Column x: one width per block, shared by both bands -----------------
  const columnWidth = new Map<string, number>();
  // A bar feeding a bar in another cell (the bypass) may be extended past its
  // right end to give that drop its own place (see `BAR_EXTENSION`): its
  // column reserves the room, so the extension never crosses the frame and
  // every block draws its bypass the same way.
  const cellOfId = new Map<string, string>();
  const busIds = new Set<string>();
  for (const cell of cells) {
    for (const node of cell.layout.nodes) {
      cellOfId.set(node.id, `${cell.columnId}|${cell.band}`);
      if (node.shape === 'bus') busIds.add(node.id);
    }
  }
  const feedsOtherBar = new Set(
    treeLinks
      .concat(cycleLinks)
      .filter(
        l =>
          busIds.has(l.source) &&
          busIds.has(l.target) &&
          cellOfId.get(l.source) !== cellOfId.get(l.target)
      )
      .map(l => l.source)
  );
  for (const cell of cells) {
    const stackWidth = Math.max(
      0,
      ...Array.from(cell.stacks.entries()).map(([parentId, kids]) => {
        const parent = cell.layout.nodesById.get(parentId);
        const widest = Math.max(
          ...kids.map(kid => measuredWidth(kid, display, layoutOptions, options))
        );
        return parent ? parent.x + SHEET_CHROME.stackIndent + widest + layoutOptions.padding : 0;
      })
    );
    const extendedBars = cell.layout.nodes
      .filter(node => feedsOtherBar.has(node.id))
      .map(
        node => node.x + node.w + BAR_EXTENSION + BAR_EXTENSION_OVERHANG + layoutOptions.padding / 2
      );
    const w = Math.max(cell.layout.width, stackWidth, 260, ...extendedBars);
    columnWidth.set(cell.columnId, Math.max(columnWidth.get(cell.columnId) ?? 0, w));
  }
  const columnX = new Map<string, number>();
  let cursorX = SHEET_CHROME.bandTitleWidth;
  for (const columnId of columnIds) {
    columnX.set(columnId, cursorX);
    cursorX += (columnWidth.get(columnId) ?? 0) + SHEET_CHROME.columnGap;
  }
  const width = cursorX - SHEET_CHROME.columnGap;

  // --- Rows per band, aligned across its columns ----------------------------
  const chromeTop = SHEET_CHROME.headerHeight + SHEET_CHROME.continuationHeight;
  const rankGap = layoutOptions.rankGapY - layoutOptions.nodeHeight;

  const nodes: PowerFlowNode[] = [];
  const conductors: PowerFlowConductor[] = [];
  const columns: SheetColumn[] = [];
  const bands: SheetBand[] = [];
  const rows: PowerFlowRankRow[] = [];
  const stackedIds = new Set<string>();
  const bridgeLinkIds = new Set<string>();
  const nodesById = new Map<string, PowerFlowNode>();
  const stackLinks: { link: PowerFlowLink; parent: PowerFlowNode; child: PowerFlowNode }[] = [];

  let bandY = 0;
  let rowOffset = 0;
  for (const band of ['input', 'output'] as BandId[]) {
    const bandCells = cells.filter(cell => cell.band === band);
    if (bandCells.length === 0) continue;

    const rankCount = Math.max(...bandCells.map(cell => cell.rankCount));
    const rowHeight: number[] = Array.from({ length: rankCount }, () => 0);
    const rowHasBus: boolean[] = Array.from({ length: rankCount }, () => false);
    for (const cell of bandCells) {
      for (const row of cell.layout.rows) {
        rowHeight[row.rank] = Math.max(rowHeight[row.rank], row.height);
        rowHasBus[row.rank] = rowHasBus[row.rank] || row.hasBus;
      }
    }
    for (let r = 0; r < rankCount; r += 1) {
      if (rowHeight[r] === 0) rowHeight[r] = layoutOptions.nodeHeight;
    }
    const rowY: number[] = [];
    let cursorY = bandY + chromeTop + layoutOptions.padding;
    for (let r = 0; r < rankCount; r += 1) {
      rowY.push(cursorY);
      cursorY += rowHeight[r] + rankGap;
    }
    for (let r = 0; r < rankCount; r += 1) {
      rows.push({ rank: rowOffset + r, y: rowY[r], height: rowHeight[r], hasBus: rowHasBus[r] });
    }

    let bandBottom = cursorY;
    for (const cell of bandCells) {
      const x0 = columnX.get(cell.columnId) ?? 0;
      const cellNodes: PowerFlowNode[] = [];
      for (const node of cell.layout.nodes) {
        const y =
          node.shape === 'bus'
            ? rowY[node.rank] + rowHeight[node.rank] - layoutOptions.busHeight
            : rowY[node.rank];
        cellNodes.push({
          ...node,
          rank: rowOffset + node.rank,
          x: node.x + x0,
          // The port moves with the node, or a symbol node's drop lands on the
          // column's origin instead of on the symbol.
          ...(node.port !== undefined ? { port: node.port + x0 } : {}),
          y,
        });
      }
      const cellById = new Map(cellNodes.map(n => [n.id, n]));

      for (const [parentId, kids] of Array.from(cell.stacks.entries())) {
        const parent = cellById.get(parentId);
        if (!parent) continue;
        let y = parent.y + parent.h + SHEET_CHROME.stackGap;
        for (const kidId of kids) {
          const vertex = verticesById.get(kidId) as PowerFlowVertex;
          const group = groupsById.get(kidId);
          const child: PowerFlowNode = {
            id: kidId,
            rank: Math.min(parent.rank + 1, rowOffset + rankCount - 1),
            shape: group ? 'group' : 'chip',
            x: parent.x + SHEET_CHROME.stackIndent,
            y,
            w: measuredWidth(kidId, display, layoutOptions, options),
            h: layoutOptions.nodeHeight,
            vertex,
            ...(group ? { group } : {}),
          };
          cellNodes.push(child);
          cellById.set(kidId, child);
          stackedIds.add(kidId);
          y += layoutOptions.nodeHeight + SHEET_CHROME.stackGap;
          const link = cell.treeLinks.find(l => l.source === parentId && l.target === kidId);
          if (link) stackLinks.push({ link, parent, child });
        }
      }

      const cellLayout: PowerFlowLayout = {
        nodes: cellNodes,
        nodesById: cellById,
        rows,
        width: 0,
        height: 0,
      };
      const routable = cell.treeLinks.filter(l => !stackedIds.has(l.target));
      conductors.push(...routePowerFlow(cellLayout, routable, cell.cycleLinks));

      for (const node of cellNodes) {
        nodes.push(node);
        nodesById.set(node.id, node);
      }
      bandBottom = Math.max(bandBottom, ...cellNodes.map(n => n.y + n.h));

      // --- The MV loop entering this column ----------------------------
      const memberSet = new Set(cell.memberIds);
      const stubs: SheetStub[] = [];
      const entering = new Set<string>();
      for (const link of treeLinks.concat(cycleLinks)) {
        // Anything feeding this column that is not on the canvas gets a stub:
        // the MV loop, and — when the blocks are paged — a riser from a block
        // on another tab. Without it a tab opens on transformers fed by
        // nothing, which is the one thing a one-line must never imply.
        if (!memberSet.has(link.target) || drawnSet.has(link.source)) continue;
        stubs.push({ nodeId: link.target, label: labelOf(link.source) });
        if (columnOf(link.source) === MV_COLUMN) entering.add(labelOf(link.source));
      }
      stubs.sort((a, b) => naturalCompare(a.label, b.label) || naturalCompare(a.nodeId, b.nodeId));

      const boardIds = band === 'input' ? inference.intakeBoardIds : inference.outputBoardIds;
      columns.push({
        id: cell.columnId,
        band,
        title: blockTitle(cell.columnId),
        meterBoardId: boardIds.find(id => memberSet.has(id)),
        memberIds: cellNodes.map(n => n.id),
        assetIds: cell.memberIds.flatMap(id => groupsById.get(id)?.memberIds ?? [id]),
        stubs,
        continuation:
          entering.size > 0
            ? `From ${Array.from(entering).sort(naturalCompare).join(', ')} · MV loop`
            : undefined,
        x: x0,
        y: bandY,
        w: columnWidth.get(cell.columnId) ?? 0,
        h: 0,
      });
    }

    const bandH = bandBottom + layoutOptions.padding + SHEET_CHROME.footerHeight - bandY;
    for (const column of columns) if (column.band === band) column.h = bandH;
    bands.push({ id: band, title: BAND_TITLES[band], y: bandY, h: bandH });
    bandY += bandH + SHEET_CHROME.bandGap;
    rowOffset += rankCount;
  }

  // --- Stacks, and the connections between columns and bands ---------------
  // A box's trunk leaves its underside near the left edge; a device drawn as
  // its symbol has a port, and the trunk must leave from it or it floats
  // beside the symbol's lead.
  for (const { link, parent, child } of stackLinks) {
    const trunkX = parent.port ?? parent.x + SHEET_CHROME.stackTrunk;
    conductors.push(buildStackConductor(link, parent, child, trunkX));
  }

  const inputBand = bands.find(b => b.id === 'input');
  const gapMid = inputBand ? inputBand.y + inputBand.h + SHEET_CHROME.bandGap / 2 : 0;
  const cellOf = (id: string) => `${columnOf(id)}|${bandOf(id)}`;

  // The gutters a bridge's long vertical may run down: the middle of every
  // gap between adjacent columns. The margins outside the first and last
  // column are a last resort — the left one carries the band titles.
  const gutters: number[] = [];
  for (const columnId of columnIds.slice(1)) {
    gutters.push((columnX.get(columnId) ?? 0) - SHEET_CHROME.columnGap / 2);
  }
  const margins = [
    (columnX.get(columnIds[0]) ?? 0) - SHEET_CHROME.columnGap / 2,
    width + SHEET_CHROME.columnGap / 2,
  ];
  // Where the conductors drawn so far join each bar; every bridge then picks
  // its own point clear of them and records it.
  const barTaps = new Map<string, BarTap[]>();
  const addTap = (id: string, tap: BarTap) => {
    if (nodesById.get(id)?.shape !== 'bus') return;
    barTaps.set(id, [...(barTaps.get(id) ?? []), tap]);
  };
  for (const conductor of conductors) {
    addTap(conductor.source, { x: conductor.points[0].x, side: 'below' });
    addTap(conductor.target, { x: conductor.points[conductor.points.length - 1].x, side: 'above' });
  }
  const routing: BridgeRouting = {
    obstacles: nodes,
    gutters,
    margins,
    barTaps,
    gutterLanes: new Map(),
    barExtension: BAR_EXTENSION,
    barLimits: barId => {
      const bar = nodesById.get(barId);
      const column =
        bar &&
        columns.find(c => bar.x >= c.x && bar.x < c.x + c.w && bar.y >= c.y && bar.y < c.y + c.h);
      return (
        column && {
          min: column.x + BAR_EXTENSION_OVERHANG,
          max: column.x + column.w - BAR_EXTENSION_OVERHANG,
        }
      );
    },
  };

  const bridges: { link: PowerFlowLink; isTie: boolean }[] = [
    ...treeLinks.map(link => ({ link, isTie: false })),
    // A tie the ranking removed keeps its identity across a column gap, so
    // the renderer styles it as a tie and never animates flow along it.
    ...cycleLinks.map(link => ({ link, isTie: true })),
  ];
  // Into a bar, the bridge from farthest outside its span goes first: it takes
  // the outer landing and the lowest turn, so the ones after it nest inside.
  // A bar as source is measured from its far end: when its drop is blocked
  // it leaves from there for a gutter, outside everything else.
  const outsideBar = ({ link }: { link: PowerFlowLink }): number => {
    const source = nodesById.get(link.source);
    const target = nodesById.get(link.target);
    if (!source || target?.shape !== 'bus') return 0;
    const xs = source.shape === 'bus' ? [source.x, source.x + source.w] : [portOf(source)];
    return Math.max(0, ...xs.map(x => Math.max(target.x - x, x - (target.x + target.w))));
  };
  bridges.sort((a, b) => outsideBar(b) - outsideBar(a));
  let bridgeRight = 0;
  for (const { link, isTie } of bridges) {
    if (!drawnSet.has(link.source) || !drawnSet.has(link.target)) continue;
    if (cellOf(link.source) === cellOf(link.target)) continue;
    const source = nodesById.get(link.source);
    const target = nodesById.get(link.target);
    if (!source || !target) continue;
    // A cross-band link turns toward its target in the gap between the bands;
    // a same-band one just above the target's row. The direct drop, when it
    // is clear, crosses just below its source.
    const crossBand = bandOf(link.source) !== bandOf(link.target);
    const targetRowTop = stackedIds.has(target.id) ? target.y : (rows[target.rank]?.y ?? target.y);
    const conductor = buildBridgeConductor(
      link,
      source,
      target,
      crossBand ? gapMid : source.y + source.h + BRIDGE_STUB,
      isTie,
      { ...routing, targetLaneY: crossBand ? gapMid : targetRowTop - BRIDGE_STUB }
    );
    // A drop out of, or onto, a bar past its end: extend the bar to meet it.
    const reach = (bar: PowerFlowNode, x: number) => {
      if (bar.shape !== 'bus') return;
      if (x > bar.x + bar.w) {
        bar.w = x + BAR_EXTENSION_OVERHANG - bar.x;
      } else if (x < bar.x) {
        bar.w += bar.x - (x - BAR_EXTENSION_OVERHANG);
        bar.x = x - BAR_EXTENSION_OVERHANG;
      }
    };
    reach(source, conductor.points[0].x);
    reach(target, conductor.points[conductor.points.length - 1].x);
    conductors.push(conductor);
    bridgeLinkIds.add(link.id);
    for (const point of conductor.points) bridgeRight = Math.max(bridgeRight, point.x);
  }

  const height = bandY - SHEET_CHROME.bandGap;
  // A bridge down the right-hand margin gutter runs past the last column.
  const stageWidth = Math.max(width, bridgeRight + SHEET_CHROME.columnGap / 2);

  return {
    split: true,
    pages: paged ? pages : [],
    pageIndex,
    sheet: {
      layout: { nodes, nodesById, rows, width: stageWidth, height },
      conductors: conductors.sort((a, b) => (a.id < b.id ? -1 : 1)),
      columns,
      bands,
      stackedIds,
      bridgeLinkIds,
    },
  };
};

/** Longest-path rank over the column's own links, as a fixpoint. */
const rankLocally = (
  members: readonly string[],
  links: readonly PowerFlowLink[]
): Map<string, number> => {
  const memberSet = new Set(members);
  const parentsOf = new Map<string, string[]>(members.map(id => [id, []]));
  for (const l of links) {
    if (memberSet.has(l.source) && memberSet.has(l.target)) parentsOf.get(l.target)?.push(l.source);
  }
  const rank = new Map<string, number>(members.map(id => [id, 0]));
  for (let pass = 0; pass < members.length; pass += 1) {
    let changed = false;
    for (const id of members) {
      const deepest = (parentsOf.get(id) ?? []).reduce(
        (max, parent) => Math.max(max, rank.get(parent) ?? 0),
        -1
      );
      if (deepest + 1 > (rank.get(id) ?? 0)) {
        rank.set(id, deepest + 1);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return rank;
};

/** A node's width by the caller's measure, clamped as the layout clamps it. */
const measuredWidth = (
  id: string,
  display: PowerFlowDisplay,
  layoutOptions: PowerFlowLayoutOptions,
  options: BuildSheetsOptions
): number => {
  const vertex = display.verticesById.get(id) as PowerFlowVertex;
  const group = display.groupsById.get(id);
  const measured = options.layout?.widthOf?.(id, group ? { vertex, group } : { vertex });
  const w =
    measured !== undefined && Number.isFinite(measured) ? measured : layoutOptions.nodeWidth;
  return Math.min(Math.max(w, layoutOptions.nodeWidth), layoutOptions.maxNodeWidth);
};

/**
 * The display restricted to one column-band, ranked from the column's own
 * roots so every column produces the same row set and the boards line up.
 */
const subDisplay = (
  display: PowerFlowDisplay,
  members: readonly string[],
  colTree: PowerFlowLink[],
  colCycle: PowerFlowLink[],
  localRank: ReadonlyMap<string, number>,
  rankCount: number,
  stacked: ReadonlySet<string>
): PowerFlowDisplay => {
  const verticesById = new Map<string, PowerFlowVertex>();
  const groupsById = new Map<string, PowerFlowLoadGroup>();
  const rankOf = new Map<string, number>();
  const ranks: string[][] = Array.from({ length: rankCount }, () => []);
  for (const id of members) {
    const vertex = display.verticesById.get(id);
    if (!vertex) continue;
    verticesById.set(id, vertex);
    const group = display.groupsById.get(id);
    if (group) groupsById.set(id, group);
    const rank = localRank.get(id) ?? 0;
    rankOf.set(id, rank);
    ranks[rank].push(id);
  }
  for (const rank of ranks) rank.sort();
  const keep = (l: PowerFlowLink) => !stacked.has(l.source) && !stacked.has(l.target);
  return {
    groups: display.groups,
    groupsById,
    groupOf: display.groupOf,
    collapsedIds: display.collapsedIds,
    verticesById,
    ranks,
    rankOf,
    treeLinks: colTree.filter(keep),
    cycleLinks: colCycle.filter(keep),
    foldedLoadsOf: display.foldedLoadsOf,
  };
};

export type { PowerFlowGraph };
